import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { conjugatePresentFormV_Salim_fromRow } from './utils/arPresentFormV_salim';
import { buildBinyan5PresentPronounSequence } from './utils/analysisPresentPronouns';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import {
  arrangeEntriesByBinyanRules,
  buildFivePlusThreeBinyanRules,
  buildPrimaryHeavyBinyanRules
} from './utils/analysisBinyanMixing';
import html2canvas from 'html2canvas';
import './VerbAnalysisGame.css';

const SOURCE_FORM_LABEL = 'המקור';
const SOURCE_PRONOUN_VALUE = '__source_no_pronoun__';
const SOURCE_PRONOUN_MESSAGE = 'אין לצורת המקור גוף';
const BINYAN_ONE_PRESENT_CHANCE = 0.6;
const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];

// מיפוי מפתחות ציווי לכינויי גוף ערביים
const IMPERATIVE_PRONOUN_MAP = {
  'you_ms': 'أَنْتَ',
  'you_fs': 'أَنْتِ',
  'you_mp': 'أَنْتُمْ',
  'you_fp': 'أَنْتُنَّ'
};

const arrangePrimaryOpeningPairsFixed = (pairs, primaryBinyan, extractBinyanNumber) => {
  const primaryPairs = pairs.filter(
    pair => extractBinyanNumber(pair.verb['בניין']) === primaryBinyan
  );
  const secondaryPairs = pairs.filter(
    pair => extractBinyanNumber(pair.verb['בניין']) !== primaryBinyan
  );
  const shuffledPrimaryPairs = [...primaryPairs].sort(() => Math.random() - 0.5);
  const guaranteedFirstPairs = shuffledPrimaryPairs.splice(0, Math.min(2, shuffledPrimaryPairs.length));

  return [...guaranteedFirstPairs, ...[...shuffledPrimaryPairs, ...secondaryPairs].sort(() => Math.random() - 0.5)];
};

const DEFAULT_ALLOWED_GIZRA_PATTERNS_BY_BINYAN = {
  '1': ['שלמים'],
  '2': ['שלמים', 'כפולים', 'ע"ו', 'פ"ו'],
  '3': ['שלמים', 'ע"ו', 'פ"ו'],
};

const getAllowedGizraPatternsForBinyan = (binyanNum, config) => {
  const configuredPatterns = config.allowedGizraPatternsByBinyan?.[binyanNum];
  if (Array.isArray(configuredPatterns) && configuredPatterns.length > 0) {
    return configuredPatterns;
  }

  return DEFAULT_ALLOWED_GIZRA_PATTERNS_BY_BINYAN[binyanNum] || ['שלמים'];
};

function VerbAnalysisGame({ gameConfig = {} }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  // קונפיגורציה ברירת מחדל - כל הבניינים בעבר
  const config = {
    title: 'ניתוח פועל - עבר',
    allowedBinyans: null, // null = כל הבניינים
    allowedTenses: ['עבר'],
    ...gameConfig
  };
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [tenseOrder, setTenseOrder] = useState([]); // סדר הזמנים עבור כל פועל (חצי עבר חצי עתיד)
  const [pronounSequence, setPronounSequence] = useState([]); // סדר הגופים - 8 גופים שונים
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [feedback, setFeedback] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showFixPrompt, setShowFixPrompt] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [pulseFields, setPulseFields] = useState({});
  const [isUserSuccess, setIsUserSuccess] = useState(false);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const answerLockedRef = React.useRef(false);

  // Create a single AudioContext for all sounds (mobile optimization)
  const audioContextRef = React.useRef(null);
  
  // Initialize AudioContext on first user interaction
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume context if suspended (required for iOS/Android)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  React.useEffect(() => {
    // Clean up on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const pronouns = [
    { arabic: 'أَنَا', hebrew: 'أَنَا - מדבר/ת' },
    { arabic: 'أَنْتَ', hebrew: 'أَنْتَ - נוכח' },
    { arabic: 'أَنْتِ', hebrew: 'أَنْتِ - נוכחת' },
    { arabic: 'هُوَ', hebrew: 'هُوَ - נסתר' },
    { arabic: 'هِيَ', hebrew: 'هِيَ - נסתרת' },
    { arabic: 'نَحْنُ', hebrew: 'نَحْنُ - מדברים/ות' },
    { arabic: 'أَنْتُمْ', hebrew: 'أَنْتُمْ - נוכחים' },
    { arabic: 'أَنْتُنَّ', hebrew: 'أَنْتُنَّ - נוכחות' },
    { arabic: 'هُمْ', hebrew: 'هُمْ - נסתרים' },
    { arabic: 'هُنَّ', hebrew: 'هُنَّ - נסתרות' }
  ];

  const forms = [
    { id: 'עבר', name: 'עבר' },
    { id: 'הווה-עתיד', name: 'הווה-עתיד' },
    { id: 'ציווי', name: 'ציווי' },
    { id: 'בינוני פועל', name: 'בינוני פועל' },
    { id: 'בינוני פעול', name: 'בינוני פעול' },
    { id: 'המקור', name: 'המקור' }
  ];

  const binyans = ['1 فَعَلَ', '2 فَعَّلَ', '3 فَاعَلَ', '4 أَفْعَلَ', '5 تَفَعَّلَ', '6 تَفَاعَلَ', '7 إِنْفَعَلَ', '8 إِفْتَعَلَ', '10 إِسْتَفْعَلَ '];

  // טעינת פעלים מה-CSV
  useEffect(() => {
    const loadVerbs = async () => {
      try {
        const rows = await loadVerbsCsv('/verbs-grade10.csv');
        
        // סינון לפי קונפיגורציה
        // הפעלים בקובץ תמיד בצורת עבר, אבל נטה אותם לפי הצורך
        let filteredVerbs = rows.filter(r => {
          const binyanNum = extractBinyanNumber(r['בניין']);
          const gizra = (r['gizra'] || '').trim();

          // סינון לפי גזרה - ברירת מחדל לפי הבניין, עם אפשרות לקונפיגורציה מקומית למשחק
          const allowedGizraPatterns = getAllowedGizraPatternsForBinyan(binyanNum, config);
          const gizraOk = allowedGizraPatterns.some(pattern => gizra.includes(pattern));
          if (!gizraOk) {
            return false;
          }

          // סינון לפי זמן - תמיד לוקחים פעלים בעבר מה-CSV
          if (r['זמן'] !== 'עבר') return false;

          // סינון לפי בניין (אם מוגדר)
          if (config.allowedBinyans) {
            if (!config.allowedBinyans.includes(binyanNum)) return false;
          }
          
          // אם יש ציווי ברשימת הזמנים, צריך פועל עם R2_FUTURE - אבל רק בבניין 1
          // בניינים 2 ו-3 בונים ציווי ישירות מהפועל/שורש ולא צריכים R2_FUTURE
          if (config.allowedTenses.includes('ציווי') && binyanNum === '1') {
            const hasFaVowelFuture = r['R2_FUTURE'] || r['fa_vowel_future'];
            if (!hasFaVowelFuture || hasFaVowelFuture.trim() === '') return false;
          }
          
          return true;
        });
        
        // בחירת 8 פעלים - עם חלוקה הוגנת בין בניינים (רק אם לא מוגבל לבניינים מסוימים)
        let selected;

        if (config.binyan3MixedConfig) {
          // חלוקה מיוחדת לבניין 3 מעורב:
          // 5 פעלים מבניין 3 (2 עבר + 2 הווה-עתיד + 1 בהגרלה)
          // 3 פעלים מבניין 1 ו-2: בניין כפול (1 עבר + 1 הווה-עתיד) + בניין יחיד (בזמן המיעוטי)
          const verbsByBinyanMix = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyanMix[bn]) verbsByBinyanMix[bn] = [];
            verbsByBinyanMix[bn].push(verb);
          });

          const b3Verbs = [...(verbsByBinyanMix['3'] || [])].sort(() => Math.random() - 0.5);
          const b1Verbs = [...(verbsByBinyanMix['1'] || [])].sort(() => Math.random() - 0.5);
          const b2Verbs = [...(verbsByBinyanMix['2'] || [])].sort(() => Math.random() - 0.5);

          // הגרלת הזמן לפועל החמישי של בניין 3
          const b3ExtraTense = Math.random() < 0.5 ? 'עבר' : 'הווה-עתיד';

          const b3Pairs = [
            { verb: b3Verbs[0], tense: 'עבר' },
            { verb: b3Verbs[1], tense: 'עבר' },
            { verb: b3Verbs[2], tense: 'הווה-עתיד' },
            { verb: b3Verbs[3], tense: 'הווה-עתיד' },
            { verb: b3Verbs[4], tense: b3ExtraTense },
          ];

          // הגרלת הבניין הכפול (1 או 2) והיחיד
          const doubleBinyanIsB1 = Math.random() < 0.5;
          const doublePool = doubleBinyanIsB1 ? b1Verbs : b2Verbs;
          const singlePool  = doubleBinyanIsB1 ? b2Verbs : b1Verbs;

          // הבניין הכפול: פועל אחד בעבר + פועל אחד בהווה-עתיד
          const doublePairs = [
            { verb: doublePool[0], tense: 'עבר' },
            { verb: doublePool[1], tense: 'הווה-עתיד' },
          ];

          // ספירת זמנים (5 מב"3 + 2 מהכפול) → קביעת הזמן המיעוטי לבניין היחיד
          const pastCount3  = b3Pairs.filter(p => p.tense === 'עבר').length + 1;
          const presCount3  = b3Pairs.filter(p => p.tense === 'הווה-עתיד').length + 1;
          const singleTense = pastCount3 <= presCount3 ? 'עבר' : 'הווה-עתיד';

          const singlePairs = [{ verb: singlePool[0], tense: singleTense }];

          // שילוב כל הזוגות
          const allPairsMix = [...b3Pairs, ...doublePairs, ...singlePairs];

          // ערבוב: הפועל הראשון תמיד מבניין 3, השאר אקראי לחלוטין
          const b3ForShuffle  = allPairsMix.filter(p => extractBinyanNumber(p.verb['בניין']) === '3');
          const nonB3ForShuffle = allPairsMix.filter(p => extractBinyanNumber(p.verb['בניין']) !== '3');

          b3ForShuffle.sort(() => Math.random() - 0.5);
          nonB3ForShuffle.sort(() => Math.random() - 0.5);

          const finalPairsMix = arrangeEntriesByBinyanRules(
            [...b3ForShuffle, ...nonB3ForShuffle],
            {
              ...buildFivePlusThreeBinyanRules('3', ['1', '2']),
              getSource: pair => extractBinyanNumber(pair.verb['בניין'])
            }
          );

          selected = finalPairsMix.map(p => p.verb);
          const tenseSeqMix = finalPairsMix.map(p => p.tense);
          const shuffledPronsMix = [...pronouns].sort(() => Math.random() - 0.5);
          const selectedPronsMix = shuffledPronsMix.slice(0, 8);

          setGameVerbs(selected);
          setTenseOrder(tenseSeqMix);
          setPronounSequence(selectedPronsMix);
          generateQuestion(selected, 0, tenseSeqMix, selectedPronsMix);
          return;
        }

        if (config.binyan4MixedConfig) {
          // חלוקה מיוחדת לבניין 4 מעורב:
          // 5 פעלים מבניין 4 (2 עבר + 2 הווה-עתיד + 1 בהגרלה)
          // ועוד פועל אחד מכל אחד מהבניינים 1, 2, 3
          const verbsByBinyan4Mix = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan4Mix[bn]) verbsByBinyan4Mix[bn] = [];
            verbsByBinyan4Mix[bn].push(verb);
          });

          const b4Pool = [...(verbsByBinyan4Mix['4'] || [])].sort(() => Math.random() - 0.5);
          const b1Pool = [...(verbsByBinyan4Mix['1'] || [])].sort(() => Math.random() - 0.5);
          const b2Pool = [...(verbsByBinyan4Mix['2'] || [])].sort(() => Math.random() - 0.5);
          const b3Pool = [...(verbsByBinyan4Mix['3'] || [])].sort(() => Math.random() - 0.5);

          const extraTense = Math.random() < 0.5 ? 'עבר' : 'הווה-עתיד';
          const primaryPairs = [
            { verb: b4Pool[0], tense: 'עבר' },
            { verb: b4Pool[1], tense: 'עבר' },
            { verb: b4Pool[2], tense: 'הווה-עתיד' },
            { verb: b4Pool[3], tense: 'הווה-עתיד' },
            { verb: b4Pool[4], tense: extraTense },
          ];

          const primaryPastCount = primaryPairs.filter(pair => pair.tense === 'עבר').length;
          const secondaryPastCount = 4 - primaryPastCount;
          const shuffledSecondaryBinyans = ['1', '2', '3'].sort(() => Math.random() - 0.5);
          const pastSecondaryBinyans = new Set(shuffledSecondaryBinyans.slice(0, secondaryPastCount));
          const secondaryPools = {
            '1': b1Pool,
            '2': b2Pool,
            '3': b3Pool,
          };
          const secondaryPairs = ['1', '2', '3'].map((binyan) => ({
            verb: secondaryPools[binyan][0],
            tense: pastSecondaryBinyans.has(binyan) ? 'עבר' : 'הווה-עתיד'
          }));

          const allPairsMix = [...primaryPairs, ...secondaryPairs];
          const analysisMixingPolicy = config.analysisMixingPolicy || {};
          const finalPairsMix = arrangeEntriesByBinyanRules(
            allPairsMix,
            {
              ...buildFivePlusThreeBinyanRules(
                analysisMixingPolicy.primaryBinyan || '4',
                analysisMixingPolicy.secondaryBinyans || ['1', '2', '3']
              ),
              getSource: pair => extractBinyanNumber(pair.verb['בניין'])
            }
          );

          selected = finalPairsMix.map(pair => pair.verb);
          const tenseSeqMix = finalPairsMix.map(pair => pair.tense);
          const shuffledPronsMix = [...pronouns].sort(() => Math.random() - 0.5);
          const selectedPronsMix = shuffledPronsMix.slice(0, 8);

          setGameVerbs(selected);
          setTenseOrder(tenseSeqMix);
          setPronounSequence(selectedPronsMix);
          generateQuestion(selected, 0, tenseSeqMix, selectedPronsMix);
          return;
        }

        if (config.binyan2AllThreeConfig) {
          // בניין 2 - שלושת הזמנים:
          // 6 פעלים מבניין 2: 2 עבר, 2 הווה-עתיד, 2 ציווי
          // 2 פעלים מבניין 1: זמנים שונים זה מזה (הגרלה)
          // שני ראשונים מבניין 2, פעלי בניין 1 לא צמודים
          const verbsByBinyan2 = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan2[bn]) verbsByBinyan2[bn] = [];
            verbsByBinyan2[bn].push(verb);
          });

          const b2Pool = [...(verbsByBinyan2['2'] || [])].sort(() => Math.random() - 0.5);
          const b1Pool = [...(verbsByBinyan2['1'] || [])].sort(() => Math.random() - 0.5);

          const b2Pairs = [
            { verb: b2Pool[0], tense: 'עבר' },
            { verb: b2Pool[1], tense: 'עבר' },
            { verb: b2Pool[2], tense: 'הווה-עתיד' },
            { verb: b2Pool[3], tense: 'הווה-עתיד' },
            { verb: b2Pool[4], tense: 'ציווי' },
            { verb: b2Pool[5], tense: 'ציווי' },
          ];

          // הגרלת שני זמנים שונים לפעלי בניין 1
          const tenseOptions2 = ['עבר', 'הווה-עתיד', 'ציווי'];
          const shuffledTenses2 = [...tenseOptions2].sort(() => Math.random() - 0.5);
          const b1Pairs = [
            { verb: b1Pool[0], tense: shuffledTenses2[0] },
            { verb: b1Pool[1], tense: shuffledTenses2[1] },
          ];

          // ערבוב פעלי בניין 2 ושמירה על שני ראשונים מבניין 2
          b2Pairs.sort(() => Math.random() - 0.5);
          const guaranteedFirst2 = b2Pairs.splice(0, 2);

          // ערבוב השאר תוך הבטחת אי-צמידות פעלי בניין 1
          let remaining2 = [...b2Pairs, ...b1Pairs];
          /* const primaryPairsForDist = pairs.filter(
            pair => extractBinyanNumber(pair.verb['׳‘׳ ׳™׳™׳']) === primaryBinyan
          );
          const secondaryPairsForDist = pairs.filter(
            pair => extractBinyanNumber(pair.verb['׳‘׳ ׳™׳™׳']) !== primaryBinyan
          );
          const shuffledPrimaryPairsForDist = [...primaryPairsForDist].sort(() => Math.random() - 0.5);
          const guaranteedFirstPairsForDist = shuffledPrimaryPairsForDist.splice(
            0,
            Math.min(2, shuffledPrimaryPairsForDist.length)
          );

          let arrangedPairsForDist = [...guaranteedFirstPairsForDist, ...shuffledPrimaryPairsForDist, ...secondaryPairsForDist];
          for (let attempt = 0; attempt < 20; attempt++) {
            arrangedPairsForDist = [
              ...guaranteedFirstPairsForDist,
              ...[...shuffledPrimaryPairsForDist, ...secondaryPairsForDist].sort(() => Math.random() - 0.5)
            ];
            const lastTwoForDist = arrangedPairsForDist.slice(-2);
            const bothSecondaryAtEnd = lastTwoForDist.length === 2 && lastTwoForDist.every(
              pair => extractBinyanNumber(pair.verb['׳‘׳ ׳™׳™׳']) !== primaryBinyan
            );
            if (!bothSecondaryAtEnd) break;
          }

          selected = arrangedPairsForDist.map(pair => pair.verb);

          const tenseSeqForDist = arrangedPairsForDist.map(pair => pair.tense);
          const shuffledPronounsForDist = [...pronouns].sort(() => Math.random() - 0.5);
          const selectedPronounsForDist = shuffledPronounsForDist.slice(0, 8);

          setGameVerbs(selected);
          setTenseOrder(tenseSeqForDist);
          setPronounSequence(selectedPronounsForDist);
          generateQuestion(selected, 0, tenseSeqForDist, selectedPronounsForDist);
          return; */
          for (let attempt = 0; attempt < 20; attempt++) {
            remaining2.sort(() => Math.random() - 0.5);
            const b1Indices = remaining2
              .map((p, i) => extractBinyanNumber(p.verb['בניין']) === '1' ? i : -1)
              .filter(i => i !== -1);
            if (b1Indices.length < 2 || Math.abs(b1Indices[0] - b1Indices[1]) > 1) break;
          }

          const finalPairs2 = [...guaranteedFirst2, ...remaining2];

          // גופים ייחודיים גלובלית: סופרים ציווי בפועל ומקצים גופים בהתאם
          const impCount2 = finalPairs2.filter(p => p.tense === 'ציווי').length;
          const nonImpCount2 = finalPairs2.length - impCount2;
          const imperativeProns2 = pronouns
            .filter(p => ['أَنْتَ', 'أَنْتِ', 'أَنْتُمْ', 'أَنْتُنَّ'].includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, impCount2);
          const usedImpArabics2 = imperativeProns2.map(p => p.arabic);
          const nonImpProns2 = pronouns
            .filter(p => !usedImpArabics2.includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, nonImpCount2);

          let impIdx2 = 0;
          let nonImpIdx2 = 0;
          const selectedProns2 = finalPairs2.map(p =>
            p.tense === 'ציווי' ? imperativeProns2[impIdx2++] : nonImpProns2[nonImpIdx2++]
          );

          const tenseSeq2 = finalPairs2.map(p => p.tense);
          const verbsSel2 = finalPairs2.map(p => p.verb);

          setGameVerbs(verbsSel2);
          setTenseOrder(tenseSeq2);
          setPronounSequence(selectedProns2);
          generateQuestion(verbsSel2, 0, tenseSeq2, selectedProns2);
          return;
        }

        if (config.binyan3AllThreeConfig) {
          // בניין 3 - שלושת הזמנים:
          // 6 פעלים מבניין 3: 2 עבר, 2 הווה-עתיד, 2 ציווי
          // 1 פועל מבניין 2 + 1 פועל מבניין 1: זמנים שונים זה מזה (הגרלה)
          // שני ראשונים מבניין 3, פועל בניין 2 ופועל בניין 1 לא צמודים
          const verbsByBinyan3 = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan3[bn]) verbsByBinyan3[bn] = [];
            verbsByBinyan3[bn].push(verb);
          });

          const b3Pool = [...(verbsByBinyan3['3'] || [])].sort(() => Math.random() - 0.5);
          const b2Pool3 = [...(verbsByBinyan3['2'] || [])].sort(() => Math.random() - 0.5);
          const b1Pool3 = [...(verbsByBinyan3['1'] || [])].sort(() => Math.random() - 0.5);

          const b3Pairs = [
            { verb: b3Pool[0], tense: 'עבר' },
            { verb: b3Pool[1], tense: 'עבר' },
            { verb: b3Pool[2], tense: 'הווה-עתיד' },
            { verb: b3Pool[3], tense: 'הווה-עתיד' },
            { verb: b3Pool[4], tense: 'ציווי' },
            { verb: b3Pool[5], tense: 'ציווי' },
          ];

          // הגרלת זמנים שונים לפועל בניין 2 ולפועל בניין 1
          const tenseOptions3 = ['עבר', 'הווה-עתיד', 'ציווי'];
          const shuffledTenses3 = [...tenseOptions3].sort(() => Math.random() - 0.5);
          const minorPairs = [
            { verb: b2Pool3[0], tense: shuffledTenses3[0] },
            { verb: b1Pool3[0], tense: shuffledTenses3[1] },
          ];

          // ערבוב פעלי בניין 3 ושמירה על שני ראשונים מבניין 3
          b3Pairs.sort(() => Math.random() - 0.5);
          const guaranteedFirst3 = b3Pairs.splice(0, 2);

          // ערבוב השאר תוך הבטחת אי-צמידות פועל בניין 2 ופועל בניין 1
          let remaining3 = [...b3Pairs, ...minorPairs];
          for (let attempt = 0; attempt < 20; attempt++) {
            remaining3.sort(() => Math.random() - 0.5);
            const minorIndices = remaining3
              .map((p, i) => extractBinyanNumber(p.verb['בניין']) !== '3' ? i : -1)
              .filter(i => i !== -1);
            if (minorIndices.length < 2 || Math.abs(minorIndices[0] - minorIndices[1]) > 1) break;
          }

          const finalPairs3 = arrangeEntriesByBinyanRules(
            [...guaranteedFirst3, ...remaining3],
            {
              ...buildPrimaryHeavyBinyanRules('3'),
              getSource: pair => extractBinyanNumber(pair.verb['בניין'])
            }
          );

          // גופים ייחודיים גלובלית: סופרים ציווי בפועל ומקצים גופים בהתאם
          const impCount3 = finalPairs3.filter(p => p.tense === 'ציווי').length;
          const nonImpCount3 = finalPairs3.length - impCount3;
          const imperativeProns3 = pronouns
            .filter(p => ['أَنْتَ', 'أَنْتِ', 'أَنْتُمْ', 'أَنْتُنَّ'].includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, impCount3);
          const usedImpArabics3 = imperativeProns3.map(p => p.arabic);
          const nonImpProns3 = pronouns
            .filter(p => !usedImpArabics3.includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, nonImpCount3);

          let impIdx3 = 0;
          let nonImpIdx3 = 0;
          const selectedProns3 = finalPairs3.map(p =>
            p.tense === 'ציווי' ? imperativeProns3[impIdx3++] : nonImpProns3[nonImpIdx3++]
          );

          const tenseSeq3 = finalPairs3.map(p => p.tense);
          const verbsSel3 = finalPairs3.map(p => p.verb);

          setGameVerbs(verbsSel3);
          setTenseOrder(tenseSeq3);
          setPronounSequence(selectedProns3);
          generateQuestion(verbsSel3, 0, tenseSeq3, selectedProns3);
          return;
        }

        if (config.binyan4AllThreeConfig) {
          // בניין 4 - שלושת הזמנים:
          // 6 פעלים מבניין 4: 2 עבר, 2 הווה-עתיד, 2 ציווי
          // ועוד שני פעלים חיצוניים: אחד מבניין 1 ואחד מבניין 2 או 3 (בהגרלה)
          const verbsByBinyan4 = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan4[bn]) verbsByBinyan4[bn] = [];
            verbsByBinyan4[bn].push(verb);
          });

          const b4Pool = [...(verbsByBinyan4['4'] || [])].sort(() => Math.random() - 0.5);
          const b1Pool = [...(verbsByBinyan4['1'] || [])].sort(() => Math.random() - 0.5);
          const extraBinyan = Math.random() < 0.5 ? '2' : '3';
          const extraPool = [...(verbsByBinyan4[extraBinyan] || [])].sort(() => Math.random() - 0.5);

          const primaryPairs = [
            { verb: b4Pool[0], tense: 'עבר' },
            { verb: b4Pool[1], tense: 'עבר' },
            { verb: b4Pool[2], tense: 'הווה-עתיד' },
            { verb: b4Pool[3], tense: 'הווה-עתיד' },
            { verb: b4Pool[4], tense: 'ציווי' },
            { verb: b4Pool[5], tense: 'ציווי' },
          ];

          const binyanOneTense = Math.random() < BINYAN_ONE_PRESENT_CHANCE
            ? 'הווה-עתיד'
            : (Math.random() < 0.5 ? 'עבר' : 'ציווי');
          const extraBinyanTense = pickRandom(
            ['עבר', 'הווה-עתיד', 'ציווי'].filter(tense => tense !== binyanOneTense)
          );
          const minorPairs = [
            { verb: b1Pool[0], tense: binyanOneTense },
            { verb: extraPool[0], tense: extraBinyanTense },
          ];

          const analysisMixingPolicy = config.analysisMixingPolicy || {};
          const finalPairs4 = arrangeEntriesByBinyanRules(
            [...primaryPairs, ...minorPairs],
            {
              ...buildPrimaryHeavyBinyanRules(analysisMixingPolicy.primaryBinyan || '4'),
              getSource: pair => extractBinyanNumber(pair.verb['בניין'])
            }
          );

          const impCount4 = finalPairs4.filter(pair => pair.tense === 'ציווי').length;
          const nonImpCount4 = finalPairs4.length - impCount4;
          const imperativeProns4 = pronouns
            .filter(p => ['أَنْتَ', 'أَنْتِ', 'أَنْتُمْ', 'أَنْتُنَّ'].includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, impCount4);
          const usedImpArabics4 = imperativeProns4.map(p => p.arabic);
          const nonImpProns4 = pronouns
            .filter(p => !usedImpArabics4.includes(p.arabic))
            .sort(() => Math.random() - 0.5)
            .slice(0, nonImpCount4);

          let impIdx4 = 0;
          let nonImpIdx4 = 0;
          const selectedProns4 = finalPairs4.map(pair =>
            pair.tense === 'ציווי' ? imperativeProns4[impIdx4++] : nonImpProns4[nonImpIdx4++]
          );

          const tenseSeq4 = finalPairs4.map(pair => pair.tense);
          const verbsSel4 = finalPairs4.map(pair => pair.verb);

          setGameVerbs(verbsSel4);
          setTenseOrder(tenseSeq4);
          setPronounSequence(selectedProns4);
          generateQuestion(verbsSel4, 0, tenseSeq4, selectedProns4);
          return;
        }

        if (config.tensePerBinyanDistribution) {
          // חלוקה מדויקת: בניין + זמן מוגדרים בצורה מפורשת
          // לדוגמה: { '2': { 'עבר': 3, 'הווה-עתיד': 3 }, '1': { 'עבר': 1, 'הווה-עתיד': 1 } }
          const verbsByBinyan = {};
          filteredVerbs.forEach(verb => {
            const bn = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan[bn]) verbsByBinyan[bn] = [];
            verbsByBinyan[bn].push(verb);
          });

          const pairs = [];
          for (const [binyan, tenseMap] of Object.entries(config.tensePerBinyanDistribution)) {
            const available = [...(verbsByBinyan[binyan] || [])].sort(() => Math.random() - 0.5);
            let idx = 0;
            for (const [tense, count] of Object.entries(tenseMap)) {
              for (let i = 0; i < count && idx < available.length; i++) {
                pairs.push({ verb: available[idx++], tense });
              }
            }
          }

          // ערבוב תוך שמירה על הצמדת פועל-זמן
          // מנסים עד 20 פעם כדי שפעלי בניין המיעוט לא יצאו צמודים
          const primaryBinyanForDist = Object.entries(config.tensePerBinyanDistribution)
            .sort((a, b) => Object.values(b[1]).reduce((s, v) => s + v, 0) - Object.values(a[1]).reduce((s, v) => s + v, 0))[0][0];
          const arrangedPairsForDist = arrangePrimaryOpeningPairsFixed(pairs, primaryBinyanForDist, extractBinyanNumber);

          let finalPairsForDist = arrangedPairsForDist;
          for (let attempt = 0; attempt < 20; attempt++) {
            finalPairsForDist = arrangePrimaryOpeningPairsFixed(pairs, primaryBinyanForDist, extractBinyanNumber);
            const lastTwoForDist = finalPairsForDist.slice(-2);
            const bothSecondaryAtEnd = lastTwoForDist.length === 2 && lastTwoForDist.every(
              pair => extractBinyanNumber(pair.verb['בניין']) !== primaryBinyanForDist
            );
            if (!bothSecondaryAtEnd) break;
          }

          selected = finalPairsForDist.map(pair => pair.verb);

          const tenseSeqForDist = finalPairsForDist.map(pair => pair.tense);
          const shuffledPronounsForDist = [...pronouns].sort(() => Math.random() - 0.5);
          const selectedPronounsForDist = shuffledPronounsForDist.slice(0, 8);

          setGameVerbs(selected);
          setTenseOrder(tenseSeqForDist);
          setPronounSequence(selectedPronounsForDist);
          generateQuestion(selected, 0, tenseSeqForDist, selectedPronounsForDist);
          return;
          /* const primaryBinyan = Object.entries(config.tensePerBinyanDistribution)
            .sort((a, b) => Object.values(b[1]).reduce((s, v) => s + v, 0) - Object.values(a[1]).reduce((s, v) => s + v, 0))[0][0];
          for (let attempt = 0; attempt < 20; attempt++) {
            pairs.splice(0, pairs.length, ...arrangeTensePerBinyanPairs(pairs, primaryBinyan, extractBinyanNumber));
            const lastTwo = pairs.slice(-2);
            const bothSecondaryAtEnd = lastTwo.length === 2 && lastTwo.every(
              extractBinyanNumber(p.verb['בניין']) === minorityBinyan &&
              extractBinyanNumber(pairs[i + 1].verb['בניין']) === minorityBinyan
            );
            if (!hasAdjacent) break;
          }
          selected = pairs.map(p => p.verb);

          const tenseSeqFromDist = pairs.map(p => p.tense);
          const shuffledPronounsDist = [...pronouns].sort(() => Math.random() - 0.5);
          const selectedPronounsDist = shuffledPronounsDist.slice(0, 8);

          setGameVerbs(selected);
          setTenseOrder(tenseSeqFromDist);
          setPronounSequence(selectedPronounsDist);
          generateQuestion(selected, 0, tenseSeqFromDist, selectedPronounsDist);
          return; */
        } else if (!config.allowedBinyans) {
          // קיבוץ לפי בניין
          const verbsByBinyan = {};
          filteredVerbs.forEach(verb => {
            const binyanNum = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan[binyanNum]) {
              verbsByBinyan[binyanNum] = [];
            }
            verbsByBinyan[binyanNum].push(verb);
          });
          
          // מציאת הבניינים הזמינים
          const availableBinyans = Object.keys(verbsByBinyan);
          const totalBinyans = availableBinyans.length;
          
          // חישוב כמה פעלים לקחת מכל בניין
          const verbsPerBinyan = Math.floor(8 / totalBinyans);
          const remainder = 8 % totalBinyans;
          
          selected = [];
          let remainderUsed = 0;
          
          // בחירה מכל בניין
          availableBinyans.forEach(binyan => {
            const verbsInBinyan = verbsByBinyan[binyan];
            const shuffled = [...verbsInBinyan].sort(() => Math.random() - 0.5);
            
            // כמות פעלים לבניין הזה (עם יחס הוגן)
            let numToTake = verbsPerBinyan;
            if (remainderUsed < remainder) {
              numToTake++;
              remainderUsed++;
            }
            
            // בחירת פעלים מהבניין
            const fromThisBinyan = shuffled.slice(0, Math.min(numToTake, shuffled.length));
            selected.push(...fromThisBinyan);
          });
          
          // ערבוב הפעלים שנבחרו כדי שלא יהיו לפי סדר בניינים
          selected = selected.sort(() => Math.random() - 0.5);
          
          // אם לא הגענו ל-8 (כי חלק מהבניינים לא היו בהם מספיק פעלים), נוסיף עוד
          if (selected.length < 8) {
            const remaining = filteredVerbs.filter(v => !selected.includes(v));
            const shuffledRemaining = remaining.sort(() => Math.random() - 0.5);
            selected.push(...shuffledRemaining.slice(0, 8 - selected.length));
          }
        } else if (config.binyanDistribution) {
          // חלוקה מוגדרת מראש לפי בניינים (למשל 5 מבניין 2 + 3 מבניין 1)
          const verbsByBinyan = {};
          filteredVerbs.forEach(verb => {
            const binyanNum = extractBinyanNumber(verb['בניין']);
            if (!verbsByBinyan[binyanNum]) {
              verbsByBinyan[binyanNum] = [];
            }
            verbsByBinyan[binyanNum].push(verb);
          });

          selected = [];
          for (const [binyan, count] of Object.entries(config.binyanDistribution)) {
            const available = verbsByBinyan[binyan] || [];
            const shuffled = [...available].sort(() => Math.random() - 0.5);
            selected.push(...shuffled.slice(0, count));
          }

          const primaryBinyan = Object.entries(config.binyanDistribution)
            .sort((a, b) => b[1] - a[1])[0][0];
          const analysisMixingPolicy = config.analysisMixingPolicy;

          if (analysisMixingPolicy?.type === 'fivePlusThree') {
            selected = arrangeEntriesByBinyanRules(
              selected.map(verb => ({ verb })),
              {
                ...buildFivePlusThreeBinyanRules(
                  analysisMixingPolicy.primaryBinyan || primaryBinyan,
                  analysisMixingPolicy.secondaryBinyans
                ),
                getSource: pair => extractBinyanNumber(pair.verb['בניין'])
              }
            ).map(pair => pair.verb);
          } else if (analysisMixingPolicy?.type === 'primaryHeavy') {
            selected = arrangeEntriesByBinyanRules(
              selected.map(verb => ({ verb })),
              {
                ...buildPrimaryHeavyBinyanRules(analysisMixingPolicy.primaryBinyan || primaryBinyan),
                getSource: pair => extractBinyanNumber(pair.verb['בניין'])
              }
            ).map(pair => pair.verb);
          } else {
            const primaryVerbs = selected.filter(v => extractBinyanNumber(v['בניין']) === primaryBinyan);
            const secondaryVerbs = selected.filter(v => extractBinyanNumber(v['בניין']) !== primaryBinyan);
            const shuffledPrimary = [...primaryVerbs].sort(() => Math.random() - 0.5);
            const guaranteedFirst = shuffledPrimary.splice(0, 2);

            let remaining = [...shuffledPrimary, ...secondaryVerbs];
            const maxAttempts = 20;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              remaining = remaining.sort(() => Math.random() - 0.5);
              const lastTwo = remaining.slice(-2);
              const bothSecondary = lastTwo.every(v => extractBinyanNumber(v['בניין']) !== primaryBinyan);
              if (!bothSecondary || secondaryVerbs.length <= 1) break;
            }

            selected = [...guaranteedFirst, ...remaining];
          }
        } else {
          // אם מוגבל לבניינים מסוימים (כמו משחק בניין 1), פשוט בחירה אקראית רגילה
          const shuffled = [...filteredVerbs].sort(() => Math.random() - 0.5);
          selected = shuffled.slice(0, 8);
        }
        
        // יצירת סדר זמנים אקראי (אם יש יותר מזמן אחד)
        let tenseSequence = [];
        let selectedPronouns = [];
        
        if (config.allowedTenses.length === 3 && 
            config.allowedTenses.includes('עבר') && 
            config.allowedTenses.includes('הווה-עתיד') && 
            config.allowedTenses.includes('ציווי')) {
          // משחק שלושה זמנים: 3 ציווי, 2 עבר, 2 הווה-עתיד, 1 הגרלה
          // יוצרים רשימת זמנים קבועה
          const fixedTenses = [
            'ציווי', 'ציווי', 'ציווי',
            'עבר', 'עבר',
            'הווה-עתיד', 'הווה-עתיד'
          ];
          
          // בוחרים זמן אקראי לשאלה ה-8
          const randomTense = config.allowedTenses[Math.floor(Math.random() * config.allowedTenses.length)];
          fixedTenses.push(randomTense);
          
          // ערבוב כל הזמנים
          tenseSequence = fixedTenses.sort(() => Math.random() - 0.5);
          
          // יצירת סדר גופים - לכל זמן גופים שונים
          // סופרים כמה שאלות מכל זמן
          const tenseCount = { 'עבר': 0, 'הווה-עתיד': 0, 'ציווי': 0 };
          tenseSequence.forEach(t => tenseCount[t]++);
          
          // בוחרים גופים ייחודיים לכל זמן
          const pronounsForTense = {
            'עבר': [...pronouns].sort(() => Math.random() - 0.5).slice(0, tenseCount['עבר']),
            'הווה-עתיד': [...pronouns].sort(() => Math.random() - 0.5).slice(0, tenseCount['הווה-עתיד']),
            'ציווי': [...pronouns].sort(() => Math.random() - 0.5).slice(0, tenseCount['ציווי'])
          };
          
          // יוצרים את רשימת הגופים לפי סדר הזמנים
          const tenseUsedIndex = { 'עבר': 0, 'הווה-עתיד': 0, 'ציווי': 0 };
          selectedPronouns = tenseSequence.map(tense => {
            const pronoun = pronounsForTense[tense][tenseUsedIndex[tense]];
            tenseUsedIndex[tense]++;
            return pronoun;
          });
        } else if (config.allowedTenses.length > 1) {
          // שני זמנים - חצי מכל זמן
          const halfCount = Math.floor(8 / config.allowedTenses.length);
          config.allowedTenses.forEach(tense => {
            for (let i = 0; i < halfCount; i++) {
              tenseSequence.push(tense);
            }
          });
          // משלימים אם יש עודפים
          while (tenseSequence.length < 8) {
            tenseSequence.push(config.allowedTenses[0]);
          }
          // ערבוב אקראי
          tenseSequence = tenseSequence.sort(() => Math.random() - 0.5);
          
          // יצירת סדר גופים - 8 גופים שונים ללא חזרות
          const shuffledPronouns = [...pronouns].sort(() => Math.random() - 0.5);
          selectedPronouns = shuffledPronouns.slice(0, 8);
        } else {
          // זמן אחד בלבד
          const shuffledPronouns = [...pronouns].sort(() => Math.random() - 0.5);
          selectedPronouns = config.binyan5PresentConfig
            ? buildBinyan5PresentPronounSequence(selected, pronouns)
            : shuffledPronouns.slice(0, 8);
        }
        
        setGameVerbs(selected);
        setTenseOrder(tenseSequence);
        setPronounSequence(selectedPronouns);
        generateQuestion(selected, 0, tenseSequence, selectedPronouns);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      }
    };
    
    loadVerbs();
  }, []);

  // פונקציה להסרת ניקוד
  const removeNiqqud = (str) => str.replace(/[\u064B-\u065F]/g, '');
  
  // פונקציה לחילוץ מספר הבניין
  const extractBinyanNumber = (binyanStr) => {
    if (!binyanStr) return null;
    const match = binyanStr.match(/^\d+/);
    return match ? match[0] : binyanStr;
  };
  
  // פונקציה להמרת מספר בניין לתצוגה עם ערבית
  const getBinyanDisplay = (binyanNumber) => {
    if (!binyanNumber) return '';
    const number = extractBinyanNumber(binyanNumber);
    const found = binyans.find(b => extractBinyanNumber(b) === number);
    return found || binyanNumber;
  };

  // פונקציה לחילוץ אותיות מהפועל
  const extractLetters = (verb) => {
    const clean = removeNiqqud(verb);
    return clean.split('').filter(c => /[\u0621-\u064A]/.test(c));
  };

  // יצירת אפשרויות שורש
  const generateRootOptions = (correctRoot, verb, binyan, pronoun) => {
    const options = [correctRoot];
    const verbLetters = extractLetters(verb);
    
    if (verbLetters.length === 3) {
      const letters = verbLetters;
      const permutations = new Set();
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          for (let k = 0; k < 3; k++) {
            if (i !== j && j !== k && i !== k) {
              const perm = letters[i] + letters[j] + letters[k];
              if (perm !== correctRoot) {
                permutations.add(perm);
              }
            }
          }
        }
      }
      const permArray = Array.from(permutations).sort(() => Math.random() - 0.5);
      options.push(...permArray.slice(0, 3));
      if (options.length < 4) {
        const arabicLetters = 'بتثجحخدذرزسشصضطظعغفقكلمنهوي'.split('');
        let fillAttempts = 0;
        while (options.length < 4 && fillAttempts < 50) {
          fillAttempts++;
          const randomLetter = arabicLetters[Math.floor(Math.random() * arabicLetters.length)];
          const pos = Math.floor(Math.random() * 3);
          const newRoot = [...letters];
          newRoot[pos] = randomLetter;
          const candidate = newRoot.join('');
          if (candidate.length === 3 && candidate !== correctRoot && !options.includes(candidate)) {
            options.push(candidate);
          }
        }
      }
      return options.sort(() => Math.random() - 0.5);
    }
    
    {
      // אלגוריתם חדש: קודם כל אותיות רצופות ודילוגים, רק אז ערבוב
      const wrongOptions = new Set();
      
      // שלב 1: נסה לקחת 3 אותיות רצופות ממיקומים שונים
      for (let i = 0; i <= verbLetters.length - 3; i++) {
        const consecutive = verbLetters.slice(i, i + 3).join('');
        if (consecutive !== correctRoot && !wrongOptions.has(consecutive)) {
          wrongOptions.add(consecutive);
        }
      }
      
      // שלב 2: נסה צירופים עם דילוגים
      if (wrongOptions.size < 3) {
        // אות ראשונה, דלג על השנייה, קח שלישית ורביעית
        if (verbLetters.length >= 4) {
          const skip1 = verbLetters[0] + verbLetters[2] + verbLetters[3];
          if (skip1 !== correctRoot && !wrongOptions.has(skip1)) {
            wrongOptions.add(skip1);
          }
        }
        
        // אות שנייה, דלג על השלישית, קח רביעית וחמישית
        if (verbLetters.length >= 5) {
          const skip2 = verbLetters[1] + verbLetters[3] + verbLetters[4];
          if (skip2 !== correctRoot && !wrongOptions.has(skip2)) {
            wrongOptions.add(skip2);
          }
        }
        
        // דילוגים נוספים
        if (verbLetters.length >= 5) {
          const skip3 = verbLetters[1] + verbLetters[2] + verbLetters[4];
          if (skip3 !== correctRoot && !wrongOptions.has(skip3)) {
            wrongOptions.add(skip3);
          }
        }
      }
      
      // שלב 3: רק אם אין מספיק אפשרויות - ערבב
      let attempts = 0;
      while (wrongOptions.size < 3 && attempts < 50) {
        attempts++;
        const shuffledLetters = [...verbLetters].sort(() => Math.random() - 0.5);
        const wrongRoot = shuffledLetters.slice(0, 3).join('');
        
        if (wrongRoot.length === 3 && wrongRoot !== correctRoot && !wrongOptions.has(wrongRoot)) {
          wrongOptions.add(wrongRoot);
        }
      }
      
      options.push(...Array.from(wrongOptions).slice(0, 3));
    }
    
    // ערבוב האפשרויות
    return options.sort(() => Math.random() - 0.5);
  };

  // יצירת שאלה חדשה
  const generateQuestion = (verbs, index, tenseSequence = null, pronounSeq = null) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }

    answerLockedRef.current = false;
    
    const verbData = verbs[index];
    
    // קובעים איזה זמן להציג - לפי הקונפיגורציה של המשחק
    // אם יש יותר מזמן אחד, משתמשים ברשימת הזמנים המסודרת (חצי-חצי)
    const targetTense = config.allowedTenses.length > 1 
      ? (tenseSequence || tenseOrder)[index]
      : config.allowedTenses[0];
    
    // בחירת פונקציית הטיה לפי הזמן הנדרש
    let conjugations;
    let pronounsForTense = pronouns; // ברירת מחדל - כל הגופים
    
    if (targetTense === 'עבר') {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
    } else if (targetTense === 'הווה-עתיד') {
      conjugations = conjugatePresentFormV_Salim_fromRow(verbData)
        || conjugatePresentFormIV_Salim_fromRow(verbData)
        || conjugatePresentFormIII_Salim_fromRow(verbData)
        || conjugatePresentFormII_Salim_fromRow(verbData)
        || conjugatePresentFormI_Salim_fromRow(verbData);
    } else if (targetTense === 'ציווי') {
      // בציווי יש רק 4 גופים - בוחרים פונקציה לפי הבניין
      const verbBinyanNum = extractBinyanNumber(verbData['בניין']);
      let imperativeConj;
      if (verbBinyanNum === '4') {
        imperativeConj = buildImperativeFromRowIV(verbData);
      } else if (verbBinyanNum === '3') {
        imperativeConj = buildImperativeFromRowIII(verbData);
      } else if (verbBinyanNum === '2') {
        imperativeConj = buildImperativeFromRowII(verbData);
      } else {
        imperativeConj = buildImperativeFromRow(verbData);
      }
      if (!imperativeConj) return;
      
      // ממירים את צורות הציווי לפורמט של כינויי גוף ערביים
      conjugations = {};
      for (const [key, value] of Object.entries(imperativeConj)) {
        const arabicPronoun = IMPERATIVE_PRONOUN_MAP[key];
        if (arabicPronoun) {
          conjugations[arabicPronoun] = value;
        }
      }
      // בציווי רק 4 גופים אפשריים
      pronounsForTense = pronouns.filter(p => 
        ['أَنْتَ', 'أَنْتِ', 'أَنْتُمْ', 'أَنْتُنَّ'].some(imp => 
          removeNiqqud(p.arabic) === removeNiqqud(imp)
        )
      );
    }
    
    if (!conjugations) return;
    
    // בחירת גוף מתוך הרשימה המסודרת (8 גופים שונים, או 4 לציווי)
    const pronounSeqToUse = pronounSeq || pronounSequence;
    let randomPronoun = pronounSeqToUse[index];
    
    // אם הגוף שנבחר לא קיים בהטיה של הפועל הזה (או לא מתאים לזמן), נבחר אחר זמין
    if (!conjugations[randomPronoun.arabic]) {
      const availablePronouns = pronounsForTense.filter(p => conjugations[p.arabic]);
      if (availablePronouns.length === 0) return;
      randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
    }
    
    const conjugatedVerb = conjugations[randomPronoun.arabic];
    
    // יצירת אפשרויות שורש
    const rootOptions = generateRootOptions(
      removeNiqqud(verbData['שורש']), 
      conjugatedVerb,
      verbData['בניין'],
      randomPronoun.arabic
    );
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      isMasdar: targetTense === SOURCE_FORM_LABEL,
      correctAnswers: {
        root: removeNiqqud(verbData['שורש']),
        binyan: verbData['בניין'],
        form: targetTense,
        pronoun: randomPronoun.arabic
      },
      options: {
        roots: rootOptions,
        binyans: binyans,
        forms: forms,
        pronouns: pronouns
      },
      pronoun: randomPronoun
    });
    
    setUserAnswers({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setFeedback({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setShowCorrectAnswer(false);
    setShowFixPrompt(false);
    setAttempts([]);
    setPulseFields({});
    setIsUserSuccess(false);
  };

  // בדיקת תשובות
  const checkAnswers = () => {
    if (!currentQuestion || answerLockedRef.current) return;
    
    const currentAttempt = { ...userAnswers };
    const newFeedback = {};
    let allCorrect = true;
    
    // שמירת מספר הניסיונות לפני הניסיון הנוכחי
    const isFirstAttempt = attempts.length === 0;
    
    // בדיקת שורש
    const isRootCorrect = userAnswers.root === currentQuestion.correctAnswers.root;
    newFeedback.root = isRootCorrect ? 'correct' : 'wrong';
    if (!isRootCorrect) allCorrect = false;
    
    // בדיקת בניין (חילוץ רק המספר להשוואה)
    const isBinyanCorrect = extractBinyanNumber(userAnswers.binyan) === extractBinyanNumber(currentQuestion.correctAnswers.binyan);
    newFeedback.binyan = isBinyanCorrect ? 'correct' : 'wrong';
    if (!isBinyanCorrect) allCorrect = false;
    
    // בדיקת צורה דקדוקית
    const isFormCorrect = userAnswers.form === currentQuestion.correctAnswers.form;
    newFeedback.form = isFormCorrect ? 'correct' : 'wrong';
    if (!isFormCorrect) allCorrect = false;
    
    // בדיקת גוף - השוואה ישירה עם ניקוד כדי להבחין בין أَنْتَ ל-أَنْتِ
    let isPronounCorrect = currentQuestion.isMasdar
      ? userAnswers.pronoun === currentQuestion.correctAnswers.pronoun
      : (userAnswers.pronoun || '') === currentQuestion.correctAnswers.pronoun;

    // אם הזמן הוא הווה-עתיד ואחד מהם أنتَ (זכר) והשני هِيَ, התשובה נכונה
    if (!isPronounCorrect && currentQuestion.correctAnswers.form === 'הווה-עתיד') {
      const userPronoun = userAnswers.pronoun || '';
      const correctPronoun = currentQuestion.correctAnswers.pronoun;

      const isUserAnta = userPronoun === 'أَنْتَ';
      const isUserHiya = userPronoun === 'هِيَ';
      const isCorrectAnta = correctPronoun === 'أَنْتَ';
      const isCorrectHiya = correctPronoun === 'هِيَ';

      // אם אחד מהם أنتَ (זכר בלבד) והשני هِيَ, התשובה נכונה
      if ((isUserAnta && isCorrectHiya) || (isUserHiya && isCorrectAnta)) {
        isPronounCorrect = true;
      }
    }
    newFeedback.pronoun = isPronounCorrect ? 'correct' : 'wrong';
    if (!isPronounCorrect) allCorrect = false;
    
    setFeedback(newFeedback);
    setAttempts(prev => [...prev, currentAttempt]);
    
    if (allCorrect) {
      // Lock immediately so a second click cannot save the same question twice.
      answerLockedRef.current = true;
      setIsUserSuccess(true);
      playSuccessSound();
      setTimeout(() => {
        // סימון הצלחה בניסיון ראשון רק אם זה באמת הניסיון הראשון
        saveToHistory(isFirstAttempt);
        nextQuestion();
      }, 2000);
    } else {
      playErrorSound();
      setShowFixPrompt(true);
    }
  };

  // שמירת תשובה בהיסטוריה
  const saveToHistory = (isCorrect) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      pronoun: currentQuestion.pronoun,
      isMasdar: currentQuestion.isMasdar,
      correctAnswers: currentQuestion.correctAnswers,
      userAttempts: [...attempts],
      finalAnswer: userAnswers,
      isCorrect
    }]);
  };

  // מעבר לשאלה הבאה
  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, nextIndex, tenseOrder, pronounSequence);
  };

  // טיפול ב"תרצו לתקן"
  const handleFix = () => {
    setShowFixPrompt(false);
    // לא מאפסים את ה-feedback - רק התאים השגויים יהיו זמינים לעריכה
  };

  // המשך ללא תיקון
  const handleContinue = () => {
    setShowFixPrompt(false);
    setShowCorrectAnswer(true);
    
    // זיהוי השדות השגויים
    const wrongFields = {};
    const updatedAnswers = { ...userAnswers };
    
    if (feedback.root === 'wrong') {
      wrongFields.root = true;
      updatedAnswers.root = currentQuestion.correctAnswers.root;
    }
    if (feedback.binyan === 'wrong') {
      wrongFields.binyan = true;
      updatedAnswers.binyan = getBinyanDisplay(currentQuestion.correctAnswers.binyan);
    }
    if (feedback.form === 'wrong') {
      wrongFields.form = true;
      updatedAnswers.form = currentQuestion.correctAnswers.form;
    }
    if (feedback.pronoun === 'wrong') {
      wrongFields.pronoun = true;
      updatedAnswers.pronoun = currentQuestion.correctAnswers.pronoun;
    }
    
    // עדכון התשובות והוספת אנימציה
    setUserAnswers(updatedAnswers);
    setPulseFields(wrongFields);
    setFeedback({
      root: 'correct',
      binyan: 'correct',
      form: 'correct',
      pronoun: 'correct'
    });
    
    saveToHistory(false);
  };
  
  // מעבר לפועל הבא אחרי צפייה בתשובה הנכונה
  const handleNextAfterCorrectAnswer = () => {
    setPulseFields({});
    setShowCorrectAnswer(false);
    nextQuestion();
  };

  // צלילים
  const handleFormChange = (newForm) => {
    const wasSourceForm = userAnswers.form === SOURCE_FORM_LABEL;
    const isSourceForm = newForm === SOURCE_FORM_LABEL;

    if (isSourceForm) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: SOURCE_PRONOUN_VALUE }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
      return;
    }

    if (wasSourceForm) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: null }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
      return;
    }

    setUserAnswers(prev => ({ ...prev, form: newForm }));
  };

  const formatPronounDisplay = (value, { forResults = false } = {}) => {
    if (!value) return '-';
    if (!value) return 'ג€”';
    if (value === SOURCE_PRONOUN_VALUE) {
      return SOURCE_PRONOUN_MESSAGE;
    }

    const pronounOption = pronouns.find(p => p.arabic === value);
    if (!pronounOption) return value;

    return `${pronounOption.arabic} ${pronounOption.hebrew.split(' - ')[1]}`;
  };

  const renderMixedResultText = (text) => {
    const value = text == null ? '' : String(text);
    const arabicSegmentPattern = /([\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF](?:[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s־-]*[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF])?)/g;
    const arabicCharPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

    return value.split(arabicSegmentPattern).map((part, index) => {
      if (!part) return null;
      if (!arabicCharPattern.test(part)) return part;

      return (
        <span key={index} className="analysis-result-arabic-part">
          {part}
        </span>
      );
    });
  };

  const playSuccessSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const playErrorSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(450, audioContext.currentTime);
    oscillator.type = 'triangle';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  // פונקציות שיתוף תוצאות
  const generateResultsImage = async () => {
    const resultsElement = document.querySelector('.words-summary');
    if (!resultsElement) return null;
    const canvas = await html2canvas(resultsElement, {
      backgroundColor: '#5f9ea0',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = 'תוצאות-ניתוח-פעלים.png';
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'תוצאות ניתוח פעלים',
          text: `ניתוח פעלים - זמן עבר\nהשגתי ${gameHistory.filter(h => h.isCorrect).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        console.log('Share canceled or failed', err);
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageData;
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = 'תוצאות-ניתוח-פעלים.png';
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  // מסך תוצאות
  if (showResults) {
    const correctCount = gameHistory.filter(h => h.isCorrect).length;
    
    return (
      <div className="App verb-analysis-results">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={() => navigate(getBackUrl())} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{config.title}</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => {
                // פורמט: פועל - שורש, בניין, צורה, גוף (עברית+ערבית)
                const binyanDisplay = getBinyanDisplay(item.correctAnswers.binyan);
                const isSourceForm = item.correctAnswers.form === SOURCE_FORM_LABEL;
                const pronounDisplay = isSourceForm ? '' : formatPronounDisplay(item.correctAnswers.pronoun, { forResults: true });
                const correctAnswerText = isSourceForm
                  ? `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}`
                  : `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}, ${pronounDisplay}`;
                
                // בניית תשובת המשתמש אם טעה
                let userAnswerText = '';
                if (!item.isCorrect && item.userAttempts.length > 0) {
                  const firstAttempt = item.userAttempts[0];
                  const userPronoun = isSourceForm ? null : pronouns.find(p => p.arabic === firstAttempt.pronoun);
                  const userParts = [];
                  userParts.push(firstAttempt.root || '—');
                  userParts.push(firstAttempt.binyan || '—');
                  userParts.push(firstAttempt.form || '—');
                  userParts.push(userPronoun ? `${userPronoun.arabic} ${userPronoun.hebrew.split(' - ')[1]}` : '—');
                  userAnswerText = userParts.join(', ');
                  if (isSourceForm) {
                    userAnswerText = userParts.slice(0, 3).join(', ');
                  }
                }
                
                return (
                  <div key={idx} className="word-item">
                    <div className="word-pair">
                      <span className="arabic-word-summary">{item.verb}</span>
                      <span className="separator"> - </span>
                      <span className="hebrew-word">
                        {renderMixedResultText(correctAnswerText)}
                      </span>
                    </div>
                    <div className={`attempts-info ${!item.isCorrect ? 'multiple-attempts' : ''}`}>
                      {item.isCorrect ? (
                        <span>✓ תשובה נכונה</span>
                      ) : (
                        <span>✗ תשובתך: {renderMixedResultText(userAnswerText)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק שוב
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="loading">טוען...</div>;
  }

  const allAnswered = userAnswers.root && userAnswers.binyan && userAnswers.form && userAnswers.pronoun;

  return (
    <div className="verb-analysis-game">
      <div className="game-header">
        <button onClick={() => navigate(getBackUrl())} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h2>{config.title}</h2>
        <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
      </div>

      <div className="verb-display">
        <div className="verb-arabic">{currentQuestion.verb}</div>
      </div>

      {/* כוכבים בהצלחה - רק כשהמשתמש הצליח בעצמו */}
      {isUserSuccess && (
        <div className="stars-animation">
          <div className="star">⭐</div>
          <div className="star">⭐</div>
          <div className="star">⭐</div>
        </div>
      )}

      <div className="analysis-grid">
        {/* שורש */}
        <div className={`analysis-section ${pulseFields.root ? 'pulse-container' : ''}`}>
          <label htmlFor="root-select">שורש</label>
          <select
            id="root-select"
            className={`analysis-dropdown ${
              feedback.root === 'wrong' ? 'wrong' : ''
            } ${feedback.root === 'correct' ? 'correct' : ''}`}
            value={userAnswers.root || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.root !== 'correct' && setUserAnswers(prev => ({ ...prev, root: e.target.value }))}
            disabled={showCorrectAnswer || showFixPrompt || feedback.root === 'correct'}
          >
            <option value="">בחר שורש...</option>
            {currentQuestion.options.roots.map((root, idx) => (
              <option key={idx} value={root}>{root}</option>
            ))}
          </select>
        </div>

        {/* בניין */}
        <div className={`analysis-section ${pulseFields.binyan ? 'pulse-container' : ''}`}>
          <label htmlFor="binyan-select">בניין</label>
          <select
            id="binyan-select"
            className={`analysis-dropdown ${
              feedback.binyan === 'wrong' ? 'wrong' : ''
            } ${feedback.binyan === 'correct' ? 'correct' : ''}`}
            value={userAnswers.binyan || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.binyan !== 'correct' && setUserAnswers(prev => ({ ...prev, binyan: e.target.value }))}
            disabled={showCorrectAnswer || showFixPrompt || feedback.binyan === 'correct'}
          >
            <option value="">בחר בניין...</option>
            {currentQuestion.options.binyans.map((binyan) => (
              <option key={binyan} value={binyan}>{binyan}</option>
            ))}
          </select>
        </div>

        {/* צורה דקדוקית */}
        <div className={`analysis-section ${pulseFields.form ? 'pulse-container' : ''}`}>
          <label htmlFor="form-select">צורה דקדוקית</label>
          <select
            id="form-select"
            className={`analysis-dropdown ${
              feedback.form === 'wrong' ? 'wrong' : ''
            } ${feedback.form === 'correct' ? 'correct' : ''}`}
            value={userAnswers.form || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.form !== 'correct' && handleFormChange(e.target.value)}
            disabled={showCorrectAnswer || showFixPrompt || feedback.form === 'correct'}
          >
            <option value="">בחר צורה...</option>
            {currentQuestion.options.forms.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </select>
        </div>

        {/* גוף */}
        <div className={`analysis-section ${pulseFields.pronoun ? 'pulse-container' : ''}`}>
          <label htmlFor="pronoun-select">גוף</label>
          <select
            id="pronoun-select"
            className={`analysis-dropdown ${
              feedback.pronoun === 'wrong' ? 'wrong' : ''
            } ${feedback.pronoun === 'correct' ? 'correct' : ''}${userAnswers.form === SOURCE_FORM_LABEL ? ' source-form-locked' : ''}`}
            value={userAnswers.pronoun || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.pronoun !== 'correct' && setUserAnswers(prev => ({ ...prev, pronoun: e.target.value }))}
            disabled={showCorrectAnswer || showFixPrompt || feedback.pronoun === 'correct' || userAnswers.form === SOURCE_FORM_LABEL}
          >
            {userAnswers.form === SOURCE_FORM_LABEL && (
              <option value={SOURCE_PRONOUN_VALUE}>{SOURCE_PRONOUN_MESSAGE}</option>
            )}
            <option value="">בחר גוף...</option>
            {currentQuestion.options.pronouns.map((pronoun) => (
              <option key={pronoun.arabic} value={pronoun.arabic}>{pronoun.hebrew}</option>
            ))}
          </select>
        </div>
      </div>

      {!showFixPrompt && !showCorrectAnswer && !isUserSuccess && Object.keys(pulseFields).length === 0 && (
        <button 
          className="check-button" 
          onClick={checkAnswers}
          disabled={!allAnswered || isUserSuccess}
        >
          בדיקה
        </button>
      )}

      {showFixPrompt && (
        <>
          <div className="overlay"></div>
          <div className="fix-prompt">
            <p>תרצו לתקן?</p>
            <div className="fix-buttons">
              <button onClick={handleFix} className="fix-yes">כן, אתקן</button>
              <button onClick={handleContinue} className="fix-no">לא, הצג תשובה נכונה</button>
            </div>
          </div>
        </>
      )}

      {Object.keys(pulseFields).length > 0 && (
        <button 
          className="check-button" 
          onClick={handleNextAfterCorrectAnswer}
        >
          המשך
        </button>
      )}

    </div>
  );
}

export default VerbAnalysisGame;
